import React, { useState, useEffect } from 'react';
import { Trophy, Award, Crown, CheckCircle, Gift, Lock } from 'lucide-react';

interface LeadGenMilestonesProps {
  totalQualifiedLeads: number;
}

const TIERS = [
  {
    icon: Trophy,
    target: 1000,
    title: 'Generate 1,000 Qualified Leads',
    reward: '100 Free Bonus Leads',
  },
  {
    icon: Award,
    target: 3000,
    title: 'Generate 3,000 Qualified Leads',
    reward: '500 Free Bonus Leads',
  },
  {
    icon: Crown,
    target: 5000,
    title: 'Generate 5,000 Qualified Leads',
    reward: '1,000 Free Bonus Leads',
  },
];

const LeadGenMilestones: React.FC<LeadGenMilestonesProps> = ({ totalQualifiedLeads }) => {
  const [burstingAchievements, setBurstingAchievements] = useState<Set<number>>(new Set());
  const [previousUnlockState, setPreviousUnlockState] = useState<boolean[]>([false, false, false]);

  const achievements = TIERS.map((tier) => {
    const progress = Math.min((totalQualifiedLeads / tier.target) * 100, 100);
    const unlocked = totalQualifiedLeads >= tier.target;
    return { ...tier, progress, unlocked };
  });

  const firstUnachievedIdx = achievements.findIndex((a) => !a.unlocked);

  useEffect(() => {
    const currentUnlockState = achievements.map((a) => a.unlocked);

    currentUnlockState.forEach((isUnlocked, index) => {
      if (isUnlocked && !previousUnlockState[index]) {
        setBurstingAchievements((prev) => new Set(prev).add(index));
        setTimeout(() => {
          setBurstingAchievements((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
        }, 2000);
      }
    });

    setPreviousUnlockState(currentUnlockState);
  }, [totalQualifiedLeads]);

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
          <Trophy className="text-white" size={22} />
        </div>
        <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Milestones & Rewards</h2>
      </div>

      <style>{`
        @keyframes subtleGlow {
          0%, 100% { box-shadow: 0 0 15px rgba(5, 224, 209, 0.2); }
          50% { box-shadow: 0 0 25px rgba(5, 224, 209, 0.3); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .celebration-glow { animation: subtleGlow 2s ease-in-out infinite; }
        .fade-in { animation: fadeIn 0.5s ease-out forwards; }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {achievements.map((item, index) => {
          const Icon = item.icon;
          const isAchieved = item.unlocked;
          const isActive = !isAchieved && index === firstUnachievedIdx;
          const isLocked = !isAchieved && !isActive;
          const isBursting = burstingAchievements.has(index);

          return (
            <div
              key={index}
              className={`
                group relative rounded-xl p-6 transition-all duration-300 cursor-pointer fade-in shadow-xl
                ${isAchieved
                  ? 'bg-gradient-to-br from-[#1875FF] via-[#05E0D1] to-[#52B5AE] celebration-glow'
                  : 'bg-gradient-to-br from-white to-[#05E0D1]/5 border-2 border-[#05E0D1]/20 hover:border-[#05E0D1] hover:shadow-2xl'
                }
                hover:scale-[1.02]
              `}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative space-y-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className={`font-extrabold text-lg mb-2 font-display ${isAchieved ? 'text-white' : 'text-[#0E1A26]'}`}>
                      {item.title}
                    </h4>
                    <p className={`text-sm font-medium ${isAchieved ? 'text-white/90' : 'text-[#0E1A26]/70'}`}>
                      {totalQualifiedLeads.toLocaleString()} / {item.target.toLocaleString()} leads
                    </p>
                  </div>
                  <div
                    className={`
                      w-12 h-12 rounded-xl flex items-center justify-center transition-all
                      ${isAchieved
                        ? 'bg-white/20'
                        : isLocked
                          ? 'bg-[#E9FBFF]'
                          : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
                      }
                    `}
                  >
                    <Icon
                      className={
                        isAchieved
                          ? 'text-white'
                          : isLocked
                            ? 'text-[#1875FF]/40'
                            : 'text-[#1875FF]'
                      }
                      size={24}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div
                    className={`relative w-full h-2.5 rounded-full overflow-hidden ${
                      isAchieved ? 'bg-white/30' : 'bg-[#E9FBFF]'
                    }`}
                  >
                    <div
                      className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${item.progress}%`,
                        background: isAchieved
                          ? '#FFFFFF'
                          : isLocked
                            ? '#CBD5E1'
                            : 'linear-gradient(90deg, #1875FF 0%, #05E0D1 100%)',
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    {isAchieved ? (
                      <div className="flex items-center space-x-1.5">
                        <CheckCircle className="text-white" size={14} />
                        <span className="font-bold text-white">ACHIEVED</span>
                      </div>
                    ) : (
                      <span className={`font-bold ${isLocked ? 'text-[#0E1A26]/40' : 'text-[#0E1A26]/60'}`}>
                        {Math.round(item.progress)}% Complete
                      </span>
                    )}
                  </div>
                </div>

                <div className={`pt-4 border-t space-y-2 ${isAchieved ? 'border-white/20' : 'border-[#1875FF]/10'}`}>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-medium ${isAchieved ? 'text-white/80' : 'text-[#0E1A26]/60'}`}>
                      Reward:
                    </span>
                    {isLocked && <Lock className="text-[#05E0D1]" size={14} />}
                    {!isLocked && !isAchieved && <Gift className="text-[#05E0D1]" size={16} />}
                  </div>
                  <p className={`font-bold text-sm ${
                    isAchieved ? 'text-white' : isLocked ? 'text-[#0E1A26]/40' : 'text-[#05E0D1]'
                  }`}>
                    {item.reward}
                    {isLocked && ' (Locked)'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeadGenMilestones;
